import { useCallback } from 'react';
import { useDataProvider, useGetIdentity } from 'react-admin';

// 1. Define the schema type for Crop Coverages data
export type CropCoverageImportSchema = {
    coverage: string;
    coverage_alias: string[]; // Add alias field
    definition: string;
    tags: string[]; // Expecting an array of strings
};

// 2. Normalization function for Crop Coverages spreadsheet
// Assumes data: Coverage (C) | Alias (D) | Definition (E) | Tags (F) - starting row 2
export function normalizeCropCoverages(
    data: Record<string, any>[]
): CropCoverageImportSchema[] {
    console.log(`Normalizing ${data.length} crop coverage records...`);
    const normalized: CropCoverageImportSchema[] = [];
    const dataArray = Array.isArray(data) ? data : [data];
    if (dataArray.length === 0) return [];

    for (const row of dataArray) {
        // Function to get cell value by assumed column letter (C=2, D=3, E=4, F=5)
        const getStrByCol = (colIndex: number, defaultValue = '') => {
            if (row && typeof row === 'object') {
                const keys = Object.keys(row);
                if (
                    keys[colIndex] !== undefined &&
                    row[keys[colIndex]] != null
                ) {
                    return String(row[keys[colIndex]]).trim();
                }
            }
            return defaultValue;
        };

        // Function to parse comma-separated strings into an array (for Alias and Tags)
        const parseToArray = (valueString: string): string[] => {
            if (!valueString) return [];
            return valueString
                .split(',')
                .map(item => item.trim())
                .filter(item => item !== '');
        };

        const coverageVal = getStrByCol(2); // Column C
        const aliasString = getStrByCol(3); // Column D
        const definitionVal = getStrByCol(4); // Column E
        const tagsString = getStrByCol(5); // Column F

        // Skip row if essential data (e.g., coverage) is missing
        if (!coverageVal) continue;

        normalized.push({
            coverage: coverageVal,
            coverage_alias: parseToArray(aliasString),
            definition: definitionVal,
            tags: parseToArray(tagsString),
        });
    }
    console.log(
        'Normalization complete for crop coverages.',
        normalized.slice(0, 5)
    ); // Log first 5
    return normalized;
}

// 3. Import hook for Crop Coverages
export function useCropCoveragesImport(selectedSpreadsheet: string) {
    const { identity } = useGetIdentity();
    const dataProvider = useDataProvider();

    const processBatch = useCallback(
        async (batch: CropCoverageImportSchema[]) => {
            const userId = identity?.id; // Still needed for meta
            if (userId === undefined || userId === null) {
                console.error(
                    'useCropCoveragesImport: Missing user ID from identity.'
                );
                throw new Error(
                    'User identity not available for record creation.'
                );
            }

            let processedCount = 0;
            for (const item of batch) {
                try {
                    await dataProvider.create('crop_coverages', {
                        // Target the new table
                        data: {
                            coverage: item.coverage,
                            coverage_alias: item.coverage_alias, // Add alias
                            definition: item.definition,
                            tags: item.tags, // Send the array directly
                            created_at: new Date().toISOString(),
                            spreadsheet_type: selectedSpreadsheet,
                        },
                        meta: { user: identity }, // Pass identity for potential RLS/policy use
                    });
                    processedCount++;
                } catch (error) {
                    console.error(
                        'useCropCoveragesImport: Failed to create crop coverage record:',
                        item,
                        'Error:',
                        error
                    );
                    throw error;
                }
            }
            console.log(
                `useCropCoveragesImport: Successfully processed ${processedCount} records in this batch.`
            );
        },
        [dataProvider, selectedSpreadsheet, identity]
    );

    return processBatch;
}
